import { WORKSPACE_PADDING } from '@components/v2/constants';
import { Alert, Box, Stack } from '@mui/material';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import { useRachelChatContext } from '../../contexts/Rachel.context';
import { useRachelRegionsContext } from '../../contexts/RachelRegions.context';
import { usePendingAgentApproval } from '../../hooks/usePendingAgentApproval';
import { usePendingToolApprovals } from '../../hooks/usePendingToolApprovals';
import { RACHEL_CONVERSATION_MAX_WIDTH } from '../../rachel.consts';
import { focusRachelInputOnClick, focusRachelInputOnMouseDown } from '../../rachel.inputFocus';
import { getRachelPartKey } from '../../rachel.utils';
import { useRachelStore } from '../../store/rachel.store';
import { selectIsRachelCompacting } from '../../store/rachel.store.selectors';
import RachelAgentApprovalCard from '../RachelAgentApprovalCard/RachelAgentApprovalCard';
import RachelCompactingCard from '../RachelCompactingCard/RachelCompactingCard';
import RachelConversationList from '../RachelConversationList/RachelConversationList';
import RachelDisclaimer from '../RachelDisclaimer/RachelDisclaimer';
import RachelDropOverlay from '../RachelDropOverlay/RachelDropOverlay';
import RachelHeader from '../RachelHeader/RachelHeader';
import RachelMessagePart from '../RachelMessagePart/RachelMessagePart';
import { getRachelChatLayoutBottomSpacing } from './rachelChatLayout.utils';
import { useRachelChatLayout } from './useRachelChatLayout';

const RachelChatLayout = () => {
  const { translate } = useTranslation();
  const { messages, status, error, startNewConversation } = useRachelChatContext();
  const { setFooterSlotNode } = useRachelRegionsContext();
  const isCompacting = useRachelStore(selectIsRachelCompacting);
  const pendingAgentApproval = usePendingAgentApproval();
  const pendingApprovals = usePendingToolApprovals();
  const {
    getRootProps,
    isDragActive,
    presentation,
    contextChipOffset,
    isConversationBottomVisible,
    setIsConversationBottomVisible,
  } = useRachelChatLayout();
  const bottomSpacing = getRachelChatLayoutBottomSpacing(presentation.mode);
  const hasPendingApproval = pendingApprovals.length > 0 || pendingAgentApproval !== null;

  return (
    <Box
      {...getRootProps({
        role: 'presentation',
        onMouseDown: focusRachelInputOnMouseDown,
        onClick: focusRachelInputOnClick,
      })}
      sx={(theme) => ({
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        padding: theme.spacing(WORKSPACE_PADDING),
        paddingBottom: `calc(${theme.spacing(bottomSpacing)} + ${contextChipOffset}px)`,
      })}
    >
      <RachelHeader onNewConversation={startNewConversation} />
      {error && <Alert severity="error">{translate(LangEnum.RACHEL_ERROR_GENERIC)}</Alert>}
      <RachelConversationList
        messages={messages}
        status={status}
        onBottomVisibilityChange={setIsConversationBottomVisible}
      />
      {isCompacting && (
        <Box
          sx={{
            flexShrink: 0,
            width: '100%',
            maxWidth: RACHEL_CONVERSATION_MAX_WIDTH,
            mx: 'auto',
            px: 2,
            pb: 1,
          }}
        >
          <RachelCompactingCard />
        </Box>
      )}
      {hasPendingApproval && (
        <Stack
          sx={{
            flexShrink: 0,
            width: '100%',
            maxWidth: RACHEL_CONVERSATION_MAX_WIDTH,
            mx: 'auto',
            gap: 1,
            pb: 1,
          }}
        >
          {pendingAgentApproval && <RachelAgentApprovalCard />}
          {pendingApprovals.map((part, index) => (
            <RachelMessagePart key={getRachelPartKey(part, index)} part={part} />
          ))}
        </Stack>
      )}
      {!hasPendingApproval && (
        <RachelDisclaimer
          hasFade={!isConversationBottomVisible}
          isOnWorkspaceSurface={presentation.mode !== 'docked'}
        />
      )}
      <Box
        ref={setFooterSlotNode}
        sx={{
          boxSizing: 'border-box',
          flexShrink: 0,
          width: '100%',
          maxWidth: RACHEL_CONVERSATION_MAX_WIDTH,
          mx: 'auto',
        }}
      />
      {isDragActive && <RachelDropOverlay />}
    </Box>
  );
};

export default RachelChatLayout;
